import type { Route } from 'nextjs-routes';

import config from 'configs/app';

const TEMPLATE_MAP: Record<Route['pathname'], string> = {
  // ── Homepage ──
  '/': '%network_name% Explorer | Blockchain Explorer & Analytics',

  // ── Transactions ──
  '/txs': '%network_name% Transactions | Blockchain Transaction History',
  '/pending-txs': '%network_name% Pending Transactions | Awaiting Confirmation',
  '/internal-txs': '%network_name% Contract Internal Transactions | Contract Calls',
  '/txs/kettle/[hash]': '%network_name% Kettle %hash% Transactions',
  '/tx/[hash]': 'Transaction %hash% | %network_name% Explorer',

  // ── Blocks ──
  '/blocks': '%network_name% Blocks | Latest Blockchain Blocks',
  '/forked-blocks': '%network_name% Forked Blocks (Reorgs)',
  '/uncle-blocks': '%network_name% Uncle Blocks',
  '/block/[height_or_hash]': 'Block %height_or_hash% | %network_name% Explorer',
  '/block/countdown': '%network_name% Block Countdown',
  '/block/countdown/[height]': '%network_name% Block %height% Countdown',

  // ── Accounts & Addresses ──
  '/accounts': '%network_name% Top Accounts | Address Rankings',
  '/accounts/label/[slug]': '%network_name% Addresses by Label | %slug%',
  '/address/[hash]': 'Address %hash% | %network_name% Explorer',
  '/address/[hash]/contract-verification': 'Verify Contract %hash% | %network_name%',

  // ── Contracts ──
  '/verified-contracts': '%network_name% Verified Contracts | Smart Contract Lookup',
  '/contract-verification': 'Verify & Publish Contract | %network_name%',

  // ── Tokens ──
  '/tokens': '%network_name% Tokens | ERC-20 Token Tracker',
  '/token/[hash]': 'Token %hash% | %network_name% Explorer',
  '/token/[hash]/instance/[id]': 'NFT Instance %id% | %network_name% Explorer',
  '/token-transfers': '%network_name% Token Transfers | Transfer History',
  '/token-flow': '%network_name% Token Flow | Holder Distribution',

  // ── DApps ──
  '/apps': '%network_name% DApps | Decentralized Applications',
  '/apps/[id]': '%network_name% DApp | Marketplace',

  // ── Stats & Charts ──
  '/stats': '%network_name% Statistics | Network Insights',
  '/stats/[id]': '%network_name% Chart | %id%',
  '/charts-stats': '%network_name% Charts & Statistics | Network Analytics',
  '/top-statistics': '%network_name% Top Statistics | Network Highlights',
  '/leaderboard': '%network_name% Leaderboard | Advanced Filter Rankings',

  // ── API & Dev Tools ──
  '/api-docs': '%network_name% API Documentation | Developer Tools',
  '/graphiql': '%network_name% GraphQL Playground | Query Data',
  '/api-plans': '%network_name% API Plans | Pricing & Access',

  // ── Search ──
  '/search-results': 'Search Results for %q% | %network_name%',

  // ── User Account ──
  '/auth/profile': 'My Profile | %network_name% Explorer',
  '/auth/verify': 'Verify Email | %network_name% Explorer',
  '/auth/verify-email-change': 'Verify Email Change | %network_name% Explorer',
  '/account/overview': 'Account Overview | %network_name% Explorer',
  '/account/settings': 'Account Settings | %network_name% Explorer',
  '/account/profile': 'Public Profile | %network_name% Explorer',
  '/account/merits': 'Merits & Rewards | %network_name% Explorer',
  '/account/watchlist': 'Watch List | %network_name% Explorer',
  '/account/api-key': 'API Keys | %network_name% Explorer',
  '/account/custom-abi': 'Custom ABI | %network_name% Explorer',
  '/account/tag-address': 'Private Address Tags | %network_name% Explorer',
  '/account/name-tags': 'Private Name Tags | %network_name% Explorer',
  '/account/txn-notes': 'Transaction Private Notes | %network_name% Explorer',
  '/account/token-ignore': 'Token Ignore List | %network_name% Explorer',
  '/account/api-dashboard': 'API Dashboard | %network_name% Explorer',
  '/account/advanced-filter': 'Advanced Filter | %network_name% Explorer',
  '/account/verified-addresses': 'My Verified Addresses | %network_name% Explorer',

  // ── Auth ──
  '/signin': 'Sign In | %network_name% Explorer',
  '/signup': 'Create Account | %network_name% Explorer',
  '/login': 'Login | %network_name% Explorer',
  '/forgot-password': 'Forgot Password | %network_name% Explorer',

  // ── Admin ──
  '/admin': 'Admin Dashboard | %network_name% Explorer',
  '/admin/users': 'User Management | %network_name% Admin',
  '/admin/tokens': 'Token Management | %network_name% Admin',
  '/admin/contracts': 'Contract Management | %network_name% Admin',
  '/admin/contacts': 'Contact Submissions | %network_name% Admin',
  '/admin/settings': 'Settings | %network_name% Admin',
  '/admin/labels': 'Label Management | %network_name% Admin',
  '/admin/name-tags': 'Name Tags Management | %network_name% Admin',
  '/admin/reports': 'Reports | %network_name% Admin',
  '/admin/approvals': 'Approvals | %network_name% Admin',
  '/admin/api': 'API Management | %network_name% Admin',
  '/admin/ads': 'Ad Management | %network_name% Admin',
  '/admin/ad-submissions': 'Ad Submissions | %network_name% Admin',
  '/admin/node-management': 'Node Management | %network_name% Admin',
  '/admin/user-activities': 'User Activities | %network_name% Admin',

  // ── Public Tags ──
  '/public-tags/submit': 'Submit Public Tag | %network_name% Explorer',

  // ── L2/Rollup ──
  '/withdrawals': '%network_name% Withdrawals | Track Withdrawals',
  '/txn-withdrawals': '%network_name% L2 to L1 Message Relayer',
  '/deposits': '%network_name% Deposits (L1 → L2)',
  '/output-roots': '%network_name% Output Roots',
  '/dispute-games': '%network_name% Dispute Games',
  '/batches': '%network_name% Transaction Batches',
  '/batches/[number]': '%network_name% Batch #%number% Details',
  '/batches/celestia/[height]/[commitment]': '%network_name% Celestia Batch %height%',

  // ── Blobs ──
  '/blobs': '%network_name% Blobs | EIP-4844 Blob Transactions',
  '/blobs/[hash]': 'Blob %hash% | %network_name% Explorer',

  // ── AA & EIP-7702 ──
  '/aa-transactions': '%network_name% Account Abstraction Transactions',
  '/eip7702-authorizations': '%network_name% EIP-7702 Authorizations',

  // ── User Operations ──
  '/ops': '%network_name% User Operations | ERC-4337',
  '/op/[hash]': 'User Operation %hash% | %network_name%',

  // ── DEX ──
  '/pools': '%network_name% DEX Pools | Liquidity & Trading',
  '/pools/[hash]': 'Pool Details | %network_name% Explorer',

  // ── Name Domains ──
  '/name-domains': '%network_name% Name Domains | Domain Registry',
  '/name-domains/[name]': '%name% Domain Details | %network_name%',
  '/domain-lookup': '%network_name% Domain Lookup | Search Domains',
  '/name-lookup': '%network_name% Name Lookup | Search Domains',

  // ── Validators ──
  '/validators': '%network_name% Validators | Top Leaderboard',
  '/validators/[id]': 'Validator %id% | %network_name% Explorer',
  '/validatorset': '%network_name% Validators Set Info | Network Snapshots',
  '/validatorset/snapshot/[id]': 'Validators Set Snapshot %id% | %network_name%',
  '/validatorsgroup': '%network_name% Validators Group',
  '/validatorsgroup/[groupName]': 'Validators Information - %groupName% | %network_name%',

  // ── NFTs ──
  '/nft-top-contracts': '%network_name% Top NFT Contracts | NFT Analytics',
  '/nft-top-mints': '%network_name% Top NFT Mints | NFT Activity',
  '/nft-latest-mints': '%network_name% Latest NFT Mints | NFT Activity',
  '/nft-trades': '%network_name% NFT Trades | NFT Marketplace',
  '/nft-transfers': '%network_name% NFT Transfers | Transfer History',

  // ── Gas ──
  '/gas-tracker': '%network_name% Gas Tracker | Real-time Gas Fees',

  // ── Misc Pages ──
  '/advanced-filter': '%network_name% Advanced Filter | Custom Search',
  '/csv-export': 'Export Data to CSV | %network_name%',
  '/visualize/sol2uml': 'Solidity UML Diagram | %network_name%',
  '/mud-worlds': '%network_name% MUD Worlds',
  '/interop-messages': '%network_name% Interop Messages',
  '/nodetracker': '%network_name% Node Tracker | Network Nodes',
  '/labelcloud': '%network_name% Label Cloud | Address Labels',
  '/code-reader': '%network_name% Code Reader | AI Contract Analysis',
  '/similar-contracts': '%network_name% Similar Contracts | Find Matches',
  '/contract-search': '%network_name% Smart Contract Search',
  '/contract-diff': '%network_name% Contract Diff | Compare Contracts',
  '/404': 'Page Not Found | %network_name% Explorer',

  // ── Tools ──
  '/tools/balance-checker': 'Balance Checker | %network_name% Tools',
  '/tools/base64': 'Base64 Encoder/Decoder | %network_name% Tools',
  '/tools/block-date': 'Block to Date Converter | %network_name% Tools',
  '/tools/broadcast-tx': 'Broadcast Transaction | %network_name% Tools',
  '/tools/csv-export': 'CSV Export | %network_name% Tools',
  '/tools/input-decoder': 'Input Data Decoder | %network_name% Tools',
  '/tools/method-id': 'Method ID Lookup | %network_name% Tools',
  '/tools/opcode': 'Opcode Tool | %network_name% Tools',
  '/tools/similar-contracts': 'Similar Contracts | %network_name% Tools',
  '/tools/token-approvals': 'Token Approvals Checker | %network_name% Tools',
  '/tools/token-standard': 'Token Standard Checker | %network_name% Tools',
  '/tools/token-supply': 'Token Supply Checker | %network_name% Tools',
  '/tools/tx-decoder': 'Transaction Decoder | %network_name% Tools',
  '/tools/unit-converter': 'Unit Converter (Wei/Gwei/Ether) | %network_name% Tools',
  '/tools/utf8': 'UTF-8 Converter | %network_name% Tools',
  '/tools/validate-onchain': 'On-chain Validator | %network_name% Tools',
  '/tools/verify-signature': 'Verify Signature | %network_name% Tools',
  '/tools/vyper': 'Vyper Compiler | %network_name% Tools',

  // ── Company/Info Pages ──
  '/about': 'About | %network_name% Explorer',
  '/advertise': 'Advertise | %network_name% Explorer',
  '/brand-assets': 'Brand Assets | %network_name% Explorer',
  '/bug-bounty': 'Bug Bounty Program | %network_name% Explorer',
  '/careers': 'Careers | %network_name% Explorer',
  '/careers/[slug]': '%slug% | Careers at %network_name%',
  '/contact': 'Contact Us | %network_name% Explorer',
  '/priority-support': 'Priority Support | %network_name% Explorer',
  '/privacy-policy': 'Privacy Policy | %network_name% Explorer',
  '/terms': 'Terms of Service | %network_name% Explorer',

  // ── Service Routes (not user-facing) ──
  '/sprite': '%network_name% SVG sprite',
  '/api/metrics': '%network_name% node API prometheus metrics',
  '/api/monitoring/invalid-api-schema': '%network_name% node API prometheus metrics',
  '/api/log': '%network_name% node API request log',
  '/api/media-type': '%network_name% node API media type',
  '/api/proxy': '%network_name% node API proxy',
  '/api/csrf': '%network_name% node API CSRF token',
  '/api/healthz': '%network_name% node API health check',
  '/api/config': '%network_name% node API app config',
  '/api/auth/[...path]': '%network_name% auth API',
  '/api/auth/profile/avatar': '%network_name% auth API avatar',
};

const TEMPLATE_MAP_ENHANCED: Partial<Record<Route['pathname'], string>> = {
  '/token/[hash]': '%symbol% (%name%) Token | %network_name% Explorer',
  '/token/[hash]/instance/[id]': '%symbol% NFT #%id% | %network_name% Explorer',
  '/apps/[id]': '%app_name% | %network_name% DApps',
  '/address/[hash]': '%domain_name% | %network_name% Explorer',
  '/stats/[id]': '%title% | %network_name% Charts',
};

export function make(pathname: Route['pathname'], isEnriched = false) {
  const template = (isEnriched ? TEMPLATE_MAP_ENHANCED[pathname] : undefined) ?? TEMPLATE_MAP[pathname] ?? '%network_name%';
  const postfix = config.meta.promoteBlockscoutInTitle ? ` | ${ config.meta.brandName }` : '';

  return (template + postfix).trim();
}
